"use client";

import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { Camera, X } from "lucide-react";
import { buscarJsonComCache, invalidarCacheCliente } from "@/lib/fetch-cache-cliente";
import { TAMANHO_MAXIMO_FOTO_PERFIL, TIPOS_FOTO_PERFIL } from "@/lib/perfil-imagem";
import { uploadWithStorage } from "@/lib/storage/client";
import { somenteDigitosTelefone, validarTelefoneUsuarioEquipe } from "@/lib/validacoes-usuario-equipe";
import { FotoPerfilUsuario } from "./AvatarComFoto";
import { Button } from "./Button";
import { Dialog } from "./Dialog";
import { useExperienciaProduto } from "./ExperienciaProvider";
import { Input } from "./Input";
import { LoadingIndicator } from "./LoadingIndicator";
import { atualizarNomeUsuarioExibido } from "./NomeUsuario";

type Perfil = {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  imagemPerfilUrl: string | null;
};

async function respostaPerfil(resposta: Response, mensagem: string) {
  const dados = await resposta.json().catch(() => ({})) as Partial<Perfil> & { erro?: string };
  if (!resposta.ok) throw new Error(dados.erro ?? mensagem);
  return dados;
}

export function PopupPerfil({ onClose }: { onClose: () => void }) {
  const { dados } = useExperienciaProduto();
  const inputFotoRef = useRef<HTMLInputElement>(null);
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [linkFoto, setLinkFoto] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  useEffect(() => {
    if (!dados?.id) return;
    let ativo = true;
    buscarJsonComCache<Perfil>("/api/perfil", { force: true, ttl: 240_000 })
      .then((resultado) => {
        if (!ativo) return;
        if (resultado.id !== dados?.id) throw new Error("Identidade do perfil divergente.");
        setPerfil(resultado);
        setNome(resultado.nome);
        setTelefone(resultado.telefone ?? "");
      })
      .catch((falha) => {
        if (ativo) setErro(falha instanceof Error ? falha.message : "Não foi possível carregar o perfil.");
      })
      .finally(() => { if (ativo) setCarregando(false); });
    return () => { ativo = false; };
  }, [dados?.id]);

  async function salvarPerfil(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (!perfil || ocupado) return;
    const nomeFinal = nome.trim();
    if (!nomeFinal) { setErro("Informe um nome para salvar."); return; }
    const telefoneFinal = somenteDigitosTelefone(telefone);
    const erroTelefone = telefoneFinal ? validarTelefoneUsuarioEquipe(telefoneFinal) : null;
    if (erroTelefone) { setErro(erroTelefone); return; }

    setOcupado(true);
    setErro("");
    setSucesso("");
    try {
      const atualizado = await respostaPerfil(await fetch("/api/perfil", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome: nomeFinal, telefone: telefoneFinal }),
      }), "Não foi possível salvar o perfil.");
      const nomeAtualizado = atualizado.nome ?? nomeFinal;
      const telefoneAtualizado = atualizado.telefone ?? null;
      setPerfil({ ...perfil, nome: nomeAtualizado, telefone: telefoneAtualizado });
      setNome(nomeAtualizado);
      setTelefone(telefoneAtualizado ?? "");
      invalidarCacheCliente("/api/me");
      invalidarCacheCliente("/api/perfil");
      atualizarNomeUsuarioExibido(nomeAtualizado);
      setSucesso("Perfil salvo.");
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : "Não foi possível salvar o perfil.");
    } finally {
      setOcupado(false);
    }
  }

  async function sincronizarFoto() {
    if (!perfil) return;
    invalidarCacheCliente("/api/perfil");
    const atualizado = await buscarJsonComCache<Perfil>("/api/perfil", { force: true, ttl: 240_000 });
    if (atualizado.id !== perfil.id) throw new Error("Identidade do perfil divergente.");
    if (!atualizado.imagemPerfilUrl) throw new Error("Não foi possível obter a foto atualizada.");
    setPerfil(atualizado);
    try { localStorage.removeItem(`bp-foto-perfil:${perfil.id}`); } catch {}
    window.dispatchEvent(new CustomEvent("bp:foto-perfil-atualizada", { detail: { usuarioId: perfil.id } }));
    setSucesso("Foto atualizada.");
  }

  async function alterarFoto(evento: ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.target.files?.[0];
    evento.target.value = "";
    if (!arquivo || !perfil || ocupado) return;
    if (!TIPOS_FOTO_PERFIL.includes(arquivo.type) || arquivo.size > TAMANHO_MAXIMO_FOTO_PERFIL) {
      setErro("Escolha uma imagem JPG, PNG ou WebP de até 2 MB.");
      return;
    }
    setOcupado(true);
    setErro("");
    setSucesso("");
    try {
      const blob = await uploadWithStorage(`perfis/${perfil.id}/avatar`, arquivo, { handleUploadUrl: "/api/perfil/foto" });
      await respostaPerfil(await fetch("/api/perfil", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imagemPerfilPathname: blob.pathname }),
      }), "Não foi possível salvar a foto no perfil.");
      await sincronizarFoto();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : "Não foi possível enviar a foto.");
    } finally {
      setOcupado(false);
    }
  }

  async function usarLinkFoto() {
    const link = linkFoto.trim();
    if (!link || !perfil || ocupado) return;
    setOcupado(true);
    setErro("");
    setSucesso("");
    try {
      await respostaPerfil(await fetch("/api/perfil", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imagemPerfilUrl: link }),
      }), "Não foi possível salvar a foto.");
      await sincronizarFoto();
      setLinkFoto("");
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : "Não foi possível salvar a foto.");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(aberto) => { if (!aberto && !ocupado) onClose(); }}
      closeOnEscape={!ocupado}
      closeOnBackdrop={!ocupado}
      aria-labelledby="titulo-popup-perfil"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id="titulo-popup-perfil" className="text-xl font-semibold">Perfil</h2>
        <Button variant="ghost" size="icon" aria-label="Fechar perfil" onClick={onClose} disabled={ocupado}><X className="h-5 w-5" /></Button>
      </div>

      {carregando ? <LoadingIndicator className="py-8">Carregando perfil...</LoadingIndicator> : perfil ? (
        <div className="mt-5 space-y-5">
          <div className="flex flex-wrap items-center gap-4">
            <FotoPerfilUsuario className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--fz-floating-hover)] text-xl font-semibold text-[var(--fz-overlay-surface-text)]">
              {dados?.marca.sigla ?? "US"}
            </FotoPerfilUsuario>
            <input ref={inputFotoRef} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" tabIndex={-1} onChange={alterarFoto} aria-label="Selecionar foto de perfil" />
            <Button variant="outline" onClick={() => inputFotoRef.current?.click()} disabled={ocupado}>
              <Camera className="h-4 w-4" /> Alterar foto
            </Button>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input type="url" value={linkFoto} onChange={(evento) => setLinkFoto(evento.target.value)} placeholder="Ou cole o link da foto" aria-label="Link da foto" disabled={ocupado} />
            <Button variant="outline" onClick={usarLinkFoto} disabled={ocupado || !linkFoto.trim()}>Usar link</Button>
          </div>
          <form onSubmit={salvarPerfil} className="space-y-4">
            <label className="block space-y-1.5 text-sm font-medium">Nome
              <Input value={nome} onChange={(evento) => setNome(evento.target.value)} autoComplete="name" disabled={ocupado} required />
            </label>
            <label className="block space-y-1.5 text-sm font-medium">E-mail
              <Input type="email" value={perfil.email} readOnly aria-readonly="true" autoComplete="email" />
            </label>
            <label className="block space-y-1.5 text-sm font-medium">Telefone
              <Input type="tel" inputMode="tel" value={telefone} onChange={(evento) => setTelefone(evento.target.value)} autoComplete="tel" disabled={ocupado} />
            </label>
            <div className="flex justify-end"><Button type="submit" disabled={ocupado}>Salvar perfil</Button></div>
          </form>
        </div>
      ) : null}
      {erro ? <p role="alert" className="mt-4 text-sm text-[var(--fz-field-error)]">{erro}</p> : null}
      {sucesso ? <p role="status" className="mt-4 text-sm">{sucesso}</p> : null}
    </Dialog>
  );
}
